import React, { useCallback, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { domainStore } from '@/stores/DomainStore';
import type { FamilyPaymentHistoryResponse, FamilyPaymentItem, FamilyPaymentType } from 'we-tree-api-client/v2';
import Spinner from '@/components/shared/Spinner';

import './billing-history-modal.css';

export interface BillingHistoryModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
}

function formatMoney(amountCents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amountCents / 100);
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency || 'USD'}`;
  }
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  }).format(date);
}

/**
 * Owner-only view of WeTree's FAMILY_PAYMENT ledger and contribution balance.
 */
const BillingHistoryModal: React.FC<BillingHistoryModalProps> = observer(({ familyId, isOpen, onClose }) => {
  const { t } = useTranslation('billing');
  const { t: tOverview } = useTranslation('overview');
  const { t: tCommon } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [history, setHistory] = useState<FamilyPaymentHistoryResponse | null>(null);

  const typeLabel = useCallback(
    (type: FamilyPaymentType | string): string => {
      const key = `history.types.${type}` as const;
      const translated = t(key);
      return translated === key ? type : translated;
    },
    [t]
  );

  const describeRow = useCallback(
    (item: FamilyPaymentItem): string => {
      const label = typeLabel(item.type);
      if (item.type === 'extension' && item.monthsApplied != null) {
        return t('history.extensionMonths', { label, count: item.monthsApplied });
      }
      if (item.type === 'contribution' && item.payerDisplayName) {
        return t('history.contributionFrom', { label, name: item.payerDisplayName });
      }
      return label;
    },
    [t, typeLabel]
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const data = await domainStore.loadBillingPaymentHistory(familyId, { page: 1, pageSize: 50 });
      setHistory(data);
    } catch (err) {
      setHistory(null);
      setErrorMessage(err instanceof Error ? err.message : t('history.loadFailed'));
    } finally {
      setIsLoading(false);
    }
  }, [familyId, t]);

  useEffect(() => {
    if (isOpen) {
      void load();
    } else {
      setHistory(null);
      setErrorMessage(null);
    }
  }, [isOpen, load]);

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose();
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
          <Dialog.Content
            className="v01-modal v01-modal--panel billing-history-modal"
            aria-labelledby="billing-history-title"
            aria-describedby="billing-history-desc"
          >
            <div className="v01-modal__header v01-modal__header--compact">
              <h2 id="billing-history-title" className="v01-modal__title">
                {tOverview('planCard.billingHistory')}
              </h2>
            </div>
            <p id="billing-history-desc" className="billing-history-modal__lede">
              {t('history.description')}
            </p>

            {isLoading ? (
              <div className="billing-history-modal__loading" aria-busy="true">
                <Spinner size="small" message="" />
              </div>
            ) : null}

            {errorMessage ? (
              <p className="billing-history-modal__error" role="alert">
                {errorMessage}
              </p>
            ) : null}

            {!isLoading && !errorMessage && history ? (
              <>
                <div className="billing-history-modal__balance" aria-live="polite">
                  <span className="billing-history-modal__balance-label">{t('history.contributionBalance')}</span>
                  <span className="billing-history-modal__balance-value">
                    {formatMoney(history.contributionBalanceCents, 'USD')}
                  </span>
                </div>

                {history.items.length === 0 ? (
                  <p className="billing-history-modal__empty">{t('history.empty')}</p>
                ) : (
                  <ul className="billing-history-modal__list">
                    {history.items.map((item) => (
                      <li key={item.id} className="billing-history-modal__row">
                        <div className="billing-history-modal__row-main">
                          <span className="billing-history-modal__row-title">{describeRow(item)}</span>
                          <span className="billing-history-modal__row-meta">
                            {formatDate(item.createdAt)}
                            {item.status !== 'completed' ? ` · ${item.status}` : ''}
                          </span>
                        </div>
                        <span
                          className={
                            item.amountCents < 0
                              ? 'billing-history-modal__amount billing-history-modal__amount--out'
                              : 'billing-history-modal__amount'
                          }
                        >
                          {formatMoney(item.amountCents, item.currency)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {history.total > history.items.length ? (
                  <p className="billing-history-modal__more">
                    {t('history.showingEntries', { shown: history.items.length, total: history.total })}
                  </p>
                ) : null}
              </>
            ) : null}

            <div className="billing-history-modal__actions">
              <Dialog.Close asChild>
                <button type="button" className="btn btn--secondary">
                  {tCommon('close')}
                </button>
              </Dialog.Close>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default BillingHistoryModal;
